/**
 * Precedent: PEPM's RACI Definition — `apps/portal/web/src/pages/project_management/workitems/
 * RaciDefinition.tsx` (+ `RaciDefinition.css`). Cloned, not imported — `project_management` is
 * another product's folder, which `CONVENTIONS.md` product independence forbids reaching into.
 *
 * SCREEN_MAP #8 — ASM-01-06, and §6.2's weighted overlap is what every cell here feeds.
 *
 * WHAT IS CLONED FROM THE PRECEDENT, in its own words (`RaciDefinition.tsx:17-20`):
 *
 *   > **ONE MATRIX, NOT PER-PHASE PANELS.** Every in-scope work item shows at once … with the
 *   > role header pinned and the work-item column frozen. That is the source's central design
 *   > decision and the CSS carries it.
 *
 * So: one matrix, the capability header pinned, the assessment-unit column frozen, every cell
 * editable in place, a legend under the toolbar, ONE Save for everything that changed, and the
 * server's own wording in a line under the toolbar rather than in a blocking `alert()`
 * (`:187-192`). The row and column axes are owner decision #14's recommendation —
 * **capabilities as columns** — which SCREEN_MAP #8 records and §7b.4's payload is shaped for:
 * the weights are carried once with the columns, never per row.
 *
 * ── THE FIVE THINGS THAT GO WRONG QUIETLY HERE ─────────────────────────────────────────
 *
 * 1. **ONE BULK SAVE PER ROW, NEVER ONE REQUEST PER CELL.** §7b.6 is built for it and says why:
 *    a row is one functional area's worth of capabilities — up to 150 — and a per-cell endpoint
 *    *"would turn one settled row into 150 requests, 150 transactions and 150 audit writes with
 *    no way to tell which of them failed."* The write is also ATOMIC per row: nothing is stored
 *    unless every cell in it passes, because *"a half-applied grid row is worse than a refused
 *    one — it scores, and it scores from a state nobody chose."* The endpoint's granularity is
 *    one unit, so Save sends one request per CHANGED ROW and reports each refusal with the row
 *    it belongs to. Rows that saved stay saved; rows that were refused keep their edits.
 *
 * 2. **PATCH SEMANTICS INSIDE THE LIST.** An absent key is untouched and an explicit `null`
 *    clears a nullable column (§7b.6), so a draft carries ONLY the fields somebody actually
 *    changed. Sending the whole cell back would blank the confidence and the comment beside the
 *    one number that was typed — which is the exact failure the contract warns about.
 *
 * 3. **NOTHING IS SCORED HERE.** Overlap, complexity, importance, the quadrant and the rank all
 *    arrive computed, and the grid payload carries each row's score and its full breakdown
 *    (§7b.4). The cell colour, the provenance tag and the N/A state are read from the server's
 *    own overlap TERM — `source` is `entered` / `override` / `not_applicable` / `missing` — so
 *    the screen never decides which of those a cell is. D-009: *"a rule implemented twice is a
 *    rule that will eventually disagree with itself."*
 *
 * 4. **N/A AND CLEARED ARE DIFFERENT STATEMENTS, AND BOTH ARE REACHABLE.** §7b.7: N/A says the
 *    capability does not apply and is scored as the configured option; a cleared cell says
 *    nothing was ever recorded and is scored as `missing`. The toggle writes the first, the ✕
 *    writes the second, *"or the grid cannot undo a mistake."* And `is_not_applicable` with an
 *    overlap beside it is refused (`not_applicable_with_overlap`) — the remedy the message names
 *    is to send `overlap_pct_tenths: null` in the same request, which is what the toggle does.
 *
 * 5. **THE GRID IS SCOPED TO ONE FUNCTIONAL AREA, AND THAT IS NOT A SIMPLIFICATION.** D-009
 *    parked the scale question with *"Revisit at Phase 3 with the real grid in front of us"* and
 *    named this as the cheap answer: *"scoping the grid to one functional area, which is the
 *    level §6.1 weights at and §6.6 rolls up at."* §7b.4 REQUIRES the area for that reason, and
 *    answers `400 field_required` without it. The business-unit filter is the other axis, which
 *    is what a 200-BU install needs.
 *
 * ── WHAT IS NOT CLONED, AND WHY ────────────────────────────────────────────────────────
 *
 *   · The precedent's own title row (`.rc-head`, a `material-icons` glyph beside an `<h1>`) is
 *     not carried: this module already has one page header for every screen
 *     (`chrome/PageHeader.tsx`, SCREEN_MAP's cross-cutting table), and a second page-chrome
 *     vocabulary inside one module is the mistake UI_CONVENTIONS §18.3 names. Icon fonts are
 *     banned by `eslint.config.js` in any case.
 *   · `height: 100%` on the wrapper. `chrome/useScreenLock.ts` measures instead — see that
 *     file's header, and CLAUDE.md "A screen header never scrolls".
 *   · Add Role / Remove Role / Add Work Item. The axes here are a BASELINED capability model and
 *     the initiative's assessment units; neither is editable from this screen, and §7b.6 refuses
 *     a capability that is not a leaf of the unit's area by name.
 *   · The phase bands. The precedent groups rows under coloured bands by delivery phase; the
 *     rows here are one functional area's assessment units and have no second grouping axis.
 *     Inventing one would be designing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
/** D-014 — initiative, functional area and business unit are all RECORD pickers. */
import {
  complexityIndex,
  capabilityComplexityTenThousandths,
  effectiveCounts,
  effectiveOverlapTenths,
  formatPct,
  quadrant as quadrantOf,
  thresholdsFromTenths,
  weightedOverlap,
  type ComplexityFactors,
  type QuadrantName,
} from '@platform/erp-scoring';
import { SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { FormPanel } from '../chrome/FormPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { erpApi, ErpApiError } from '../api';
import { canEditCapabilityModel, canEnterAssessments } from '../roles';
import { CHIP_INFO, CHIP_NEUTRAL, CHIP_SUCCESS, type ChipPair } from '../chips';
import { QUADRANT_CHIP } from '../quadrantChips';
import { tenthsToText, textToTenths } from '../weights';
import {
  type AssessmentGridPayload,
  type AssessmentGridRow,
  type BusinessUnit,
  type AssessmentGridListRow,
  type CapabilityModelVersionRow,
  type CapabilityAssessment,
  type CapabilityAssessmentWrite,
  type ErpContext,
  type FunctionalAreaOption,
  type GridCapability,
  type Initiative,
  type OverlapTerm,
  type ScoringConfig,
  type UnitScore,
} from '../types';
import { CalculationBreakdown } from '../scores/CalculationBreakdown';
import './AssessmentGrid.css';

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED from `BUILD_INSTRUCTIONS.md` §6.2 and §6.3, with the tone of
 * `settings/ScoringModel.tsx:17-41` and the honesty of `ScoringAuditTrail.tsx:51-52` about what
 * the screen does not do (UI_CONVENTIONS §3.7).
 * ------------------------------------------------------------------------- */

const AssessmentGridHelp: Help = {
  title: 'Assessment Grid',
  icon: '🧩',
  purpose:
    'One row per <strong>assessment unit</strong> — a business unit crossed with the system it runs — and one column per <strong>capability</strong> of the functional area you are assessing. In each cell you record how much of that capability the system covers, <strong>as a percentage</strong>, and the weighted result is the unit&rsquo;s overlap score.',
  doHere: [
    'Pick an <strong>initiative</strong> and one <strong>functional area</strong>. The grid is deliberately one area at a time: that is the level the capability model is weighted at, and the level the roll-ups are defined at.',
    'Narrow to one <strong>business unit</strong> when the list is long. The scores never change when you filter — importance is relative to the whole initiative, not to what is on screen.',
    'Type an <strong>overlap percentage</strong> into a cell. The weight above each column is the share that capability carries within the unit, and the weights of one unit always total 100.0%.',
    'Press <strong>N/A</strong> when a capability does not apply to that unit at all. That is a <em>statement</em>, and it is scored by the N/A rule on the Scoring Definition screen — it is not the same as leaving the cell empty.',
    'Type how many <strong>systems</strong> and <strong>interfaces</strong> support that capability in the two small fields under the overlap. The cell then shows its own <strong>complexity</strong>, from the bands on the Scoring Definition screen.',
    'Open <strong>&hellip;</strong> on a cell for the rest of it: an <strong>override</strong> for any of the three figures (each one needs a reason), a <strong>confidence</strong> and a <strong>comment</strong>.',
    'While a row has unsaved changes, its scores show as a <strong>preview</strong> of what Save will produce, and every changed cell has an orange outline.',
    '<strong>Save</strong> writes every row you changed. Each row is saved whole: either every cell in it is stored or none of it is. Unsaved changes are kept if you switch runs or leave the screen, and the browser asks before you close the tab.',
    'Use <strong>Explain</strong> beside a unit to see every term of its score and where each value came from.',
  ],
  fits: 'ERP Consolidation &rarr; Assessment Grid',
  firstTime:
    'Fill one row across, then open Explain on it — the breakdown shows exactly which cell moved the score and by how much.',
  sections: [
    {
      type: 'defs',
      label: 'What a cell can say',
      items: [
        {
          term: 'A %',
          color: '#0d652d',
          def: 'How much of this capability the system covers in this business unit, from <strong>1%</strong> to <strong>100%</strong>.',
        },
        {
          term: 'N/A',
          color: '#3c4043',
          def: 'The capability does not apply here. It is scored by the <strong>N/A rule</strong>: either as 0% keeping its weight, or excluded with the remaining weights scaled back to 100% and the coverage shown.',
        },
        {
          term: 'Empty',
          color: '#8c3a00',
          def: 'Nothing recorded. Scored as <strong>0% in the lowest complexity band</strong> and still counted, so a half-filled grid reads as a low score with a visible reason rather than as a high score over the cells that happen to exist.',
        },
        {
          term: 'Override',
          color: '#0842a0',
          def: 'A figure replaced on purpose, with a <strong>reason that is required</strong>. The entered value is kept beside it, and the breakdown says which one was scored.',
        },
      ],
      note: 'Everything in this module is a percentage — there is no 1&ndash;5 scale anywhere in it.',
    },
    {
      type: 'html',
      label: 'What this screen will not do for you',
      html: 'There is no <strong>paste from Excel</strong> and no arrow-key range selection: nothing in this product reads the clipboard, and both would be new interactions rather than ported ones. Tab moves between cells, which is what the screens this one is cloned from do. It also does not change a quadrant directly — a quadrant is calculated from overlap and complexity, so you move a unit by correcting the figure that put it there.',
    },
  ],
};

/* ---------------------------------------------------------------------------
 * Cell state
 * ------------------------------------------------------------------------- */

/** A draft is keyed `unitId` → `capabilityId` → the fields that actually changed. */
type Drafts = Record<string, Record<string, CapabilityAssessmentWrite>>;

const draftFor = (
  drafts: Drafts,
  unitId: string,
  capabilityId: string,
): CapabilityAssessmentWrite | undefined => drafts[unitId]?.[capabilityId];

/** Merge one field into the draft for a cell, leaving every other field untouched. */
function withDraft(
  drafts: Drafts,
  unitId: string,
  capabilityId: string,
  change: Omit<CapabilityAssessmentWrite, 'capability_id'>,
): Drafts {
  const row = drafts[unitId] ?? {};
  const cell = row[capabilityId] ?? { capability_id: capabilityId };
  return { ...drafts, [unitId]: { ...row, [capabilityId]: { ...cell, ...change } } };
}

const draftedRows = (drafts: Drafts): [string, CapabilityAssessmentWrite[]][] =>
  Object.entries(drafts)
    .map(([unitId, cells]): [string, CapabilityAssessmentWrite[]] => [unitId, Object.values(cells)])
    .filter(([, cells]) => cells.length > 0);

/**
 * UNSAVED EDITS SURVIVE THE SCREEN — owner, 2026-10-09, after values typed into a run were never
 * stored: *"I need a Save button on Assessment Grid"*. Save stays the only thing that writes, but
 * nothing may throw an edit away silently any more. Each run's unsaved edits are kept in the
 * tab's `sessionStorage` under its own key, so switching runs, leaving the screen through the
 * nav, or a refresh brings them back; closing or refreshing the tab with edits pending asks first
 * (`beforeunload`). Storage can be unavailable (a private window, blocked site data), so every
 * read and write is guarded and the screen still works without it.
 */
const draftStorageKey = (gridId: string): string => `erp-assessment-grid-drafts:${gridId}`;

function readStoredDrafts(key: string): Drafts {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' ? (parsed as Drafts) : {};
  } catch {
    return {};
  }
}

function writeStoredDrafts(key: string, drafts: Drafts): void {
  try {
    if (draftedRows(drafts).length === 0) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(drafts));
  } catch {
    /* Storage unavailable — the edits still live on this screen until it closes. */
  }
}

/**
 * The colour a cell wears — difference 4 in `AssessmentGrid.css`'s header.
 *
 * `cellStyle` in the precedent (`RaciDefinition.tsx:103-110`) is a pair per VALUE; SCREEN_MAP #8
 * says that becomes "a band ramp read from `ScoringConfig`, never a literal". `scoring_config`
 * carries no overlap bands — it carries the overlap THRESHOLD — so the comparison the
 * configuration actually defines is the one drawn here, and the pairs are §1.9.2's measured ones.
 *
 * NOT RECORDED reuses the precedent's own blank pair (`:109`), which is what UI_GAPS §5 proposes.
 */
const NOT_RECORDED: ChipPair = { bg: '#fff', fg: '#cfd8dc' };
/* The comparison itself is drawn in `renderCell`, from the value as TYPED rather than the last
   saved score — same three outcomes, same pairs (owner, 2026-10-09, from the grid prototype). */

/** The tag beside a cell. The server's own `source`, not a word this screen chose. */
const SOURCE_TAG: Record<OverlapTerm['source'], string | null> = {
  entered: null,
  override: 'override',
  not_applicable: 'n/a',
  missing: 'empty',
};

/* ---------------------------------------------------------------------------
 * The cell dialog's form — strings, so a half-typed number stays typable
 * ------------------------------------------------------------------------- */

interface CellForm {
  is_not_applicable: boolean;
  overlap: string;
  systems: string;
  interfaces: string;
  overlapOverride: string;
  overlapOverrideReason: string;
  systemsOverride: string;
  systemsOverrideReason: string;
  interfacesOverride: string;
  interfacesOverrideReason: string;
  confidence: string;
  comments: string;
}

const pctText = (tenths: number | null | undefined): string =>
  tenths === null || tenths === undefined ? '' : tenthsToText(tenths);
const intText = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);
const intOrNull = (text: string): number | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? Math.round(value) : null;
};
const textOrNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

/** The cell as it stands: what is stored, with any unsaved draft on top of it. */
function seedForm(
  stored: CapabilityAssessment | undefined,
  draft: CapabilityAssessmentWrite | undefined,
): CellForm {
  const pick = <K extends keyof CapabilityAssessment & keyof CapabilityAssessmentWrite>(
    key: K,
  ): CapabilityAssessment[K] | undefined =>
    draft !== undefined && Object.hasOwn(draft, key)
      ? (draft[key] as CapabilityAssessment[K])
      : stored?.[key];

  return {
    is_not_applicable: pick('is_not_applicable') === true,
    overlap: pctText(pick('overlap_pct_tenths')),
    systems: intText(pick('systems_count')),
    interfaces: intText(pick('interfaces_count')),
    overlapOverride: pctText(pick('overlap_override_pct_tenths')),
    overlapOverrideReason: pick('overlap_override_reason') ?? '',
    systemsOverride: intText(pick('systems_count_override')),
    systemsOverrideReason: pick('systems_count_override_reason') ?? '',
    interfacesOverride: intText(pick('interfaces_count_override')),
    interfacesOverrideReason: pick('interfaces_count_override_reason') ?? '',
    confidence: pctText(pick('confidence_pct_tenths')),
    comments: pick('comments') ?? '',
  };
}

/**
 * The form back into a patch — **only the fields that differ from what is stored**.
 *
 * This is where §7b.6's per-cell PATCH semantics are honoured. Sending every field would blank
 * the two the user never opened, and an explicit `null` is how a nullable column is CLEARED, so
 * "unchanged" and "cleared" cannot be the same thing on the wire.
 */
function formToPatch(
  form: CellForm,
  stored: CapabilityAssessment | undefined,
): CapabilityAssessmentWrite {
  const patch: CapabilityAssessmentWrite = { capability_id: '' };
  const put = <K extends keyof CapabilityAssessmentWrite>(
    key: K,
    next: CapabilityAssessmentWrite[K],
    current: unknown,
  ) => {
    if ((next ?? null) !== (current ?? null)) patch[key] = next;
  };
  /**
   * An EMPTY numeric field means `null`, which §7b.6 reads as "clear this column" — and because
   * every one of these inputs is `type="number"`, unparseable text never reaches here: the
   * browser hands back `''`. The inline cell, where someone types freely, guards the same
   * distinction explicitly in `typeOverlap`.
   */

  put('is_not_applicable', form.is_not_applicable, stored?.is_not_applicable ?? false);
  put('overlap_pct_tenths', textToTenths(form.overlap), stored?.overlap_pct_tenths);
  put('systems_count', intOrNull(form.systems), stored?.systems_count);
  put('interfaces_count', intOrNull(form.interfaces), stored?.interfaces_count);
  put(
    'overlap_override_pct_tenths',
    textToTenths(form.overlapOverride),
    stored?.overlap_override_pct_tenths,
  );
  put(
    'overlap_override_reason',
    textOrNull(form.overlapOverrideReason),
    stored?.overlap_override_reason,
  );
  put('systems_count_override', intOrNull(form.systemsOverride), stored?.systems_count_override);
  put(
    'systems_count_override_reason',
    textOrNull(form.systemsOverrideReason),
    stored?.systems_count_override_reason,
  );
  put(
    'interfaces_count_override',
    intOrNull(form.interfacesOverride),
    stored?.interfaces_count_override,
  );
  put(
    'interfaces_count_override_reason',
    textOrNull(form.interfacesOverrideReason),
    stored?.interfaces_count_override_reason,
  );
  put('confidence_pct_tenths', textToTenths(form.confidence), stored?.confidence_pct_tenths);
  put('comments', textOrNull(form.comments), stored?.comments);
  return patch;
}

/**
 * The one check this dialog makes before it closes, and the reason it makes it locally.
 *
 * §5.3's rule is that validation is server-first, and it is honoured everywhere else on this
 * screen: §7b.6 refuses a range, a scale slip and a contradiction by name and the message is
 * shown as written. But the dialog does not write — Save does — so a missing reason would
 * otherwise surface minutes later against a row rather than against the field. This states the
 * requirement at the field; the server still refuses independently on the merged state, which is
 * the check that counts.
 */
function missingOverrideReason(form: CellForm): string | null {
  if (textToTenths(form.overlapOverride) !== null && form.overlapOverrideReason.trim() === '') {
    return 'An overlap override needs a reason.';
  }
  if (intOrNull(form.systemsOverride) !== null && form.systemsOverrideReason.trim() === '') {
    return 'A systems-count override needs a reason.';
  }
  if (intOrNull(form.interfacesOverride) !== null && form.interfacesOverrideReason.trim() === '') {
    return 'An interfaces-count override needs a reason.';
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------- */

type Notice = { kind: 'ok' | 'err'; text: string } | null;

export function AssessmentGrid() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  /*
   * THE RUN SUPPLIES THE INITIATIVE. `?initiative=` is still read so older links resolve, but a
   * run names its own, which is what let the initiative picker go.
   */
  const initiativeParam = searchParams.get('initiative') ?? '';
  /*
   * WHICH MODEL IS BEING ASSESSED — `0106`, and the owner's *"On Assessment grid I should select
   * Baseline model nothing else"*.
   *
   * Before draft was removed an initiative had at most one model in force, so the grid resolved
   * it silently and the screen never said which one you were looking at. With several models all
   * in force that silence becomes a real hazard: two models on one initiative, and no way to tell
   * which your percentages were recorded against. It is in the URL for the same reason the area
   * is — so a shared link opens the same grid.
   */
  /*
   * `0107` — THE RUN IS THE SCOPE.
   *
   * `?grid=` names a saved assessment run, which carries its own capability model, functional
   * area and business unit. The older `?model=`, `?area=` and `?bu=` still resolve, so saved
   * links keep working, but a run supersedes them when present: it is the thing the owner
   * creates, names and versions, and two versions of one scope would otherwise interleave.
   */
  const gridParam = searchParams.get('grid') ?? '';
  const modelParam = searchParams.get('model');
  const areaParam = searchParams.get('area');
  const businessUnitParam = searchParams.get('bu') ?? '';

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  /** Every capability model of the chosen initiative — all of them are in force (`0106`). */
  const [models, setModels] = useState<CapabilityModelVersionRow[]>([]);
  /** The saved runs of this initiative (`0107`). */
  const [runs, setRuns] = useState<AssessmentGridListRow[]>([]);
  const [businessUnitsAll, setBusinessUnitsAll] = useState<BusinessUnit[]>([]);
  /*
   * CREATING A RUN FROM THIS SCREEN.
   *
   * It is also on Assessment Runs, and that is deliberate duplication rather than an oversight:
   * this is the screen somebody arrives at wanting to assess, and the owner arrived at it four
   * times looking for the control. A create action belongs where the need is discovered, not
   * only where the housekeeping lives. Both call the same endpoint.
   */
  const [newRun, setNewRun] = useState<{
    name: string;
    description: string;
    capabilityModelId: string;
    areaId: string;
    businessUnitId: string;
  } | null>(null);
  const [newRunError, setNewRunError] = useState<string | null>(null);
  /*
   * The model being assessed: the URL's if it names one, otherwise the newest. DERIVED rather
   * than written back, so this cannot loop — the same arrangement `areaId` uses below, and the
   * one the capability model editor uses for its version.
   */
  const run = runs.find((row) => row.id === gridParam) ?? null;
  /*
   * A RUN WINS OVER THE LOOSE PARAMETERS. It carries the model, the area and the business unit,
   * so when one is open those three are read from it rather than from the URL — which is what
   * stops a saved link's stale `?area=` pointing somewhere the run does not cover.
   */
  const modelId = run?.capability_model_id ?? modelParam ?? '';
  const initiativeId = run?.initiative_id ?? initiativeParam;
  const [areas, setAreas] = useState<FunctionalAreaOption[]>([]);
  const [grid, setGrid] = useState<AssessmentGridPayload | null>(null);
  /** The initiative's scoring definition — bands and factors for the live per-cell and per-row preview. */
  const [scoringConfig, setScoringConfig] = useState<ScoringConfig | null>(null);

  /**
   * §6.1's gate, as the server words it. One code, three messages (§7b.0) — no model at all, a
   * draft that does not balance, a draft that balances — and a client's branch is the same in
   * all three, so this holds the message rather than classifying it.
   */
  const [gate, setGate] = useState<string | null>(null);

  const [drafts, setDrafts] = useState<Drafts>({});
  /** The cell being typed in — raw text, so "4." and "" remain expressible. */
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);

  const [loading, setLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const [dialog, setDialog] = useState<{
    unit: AssessmentGridRow;
    capability: GridCapability;
    form: CellForm;
    error: string | null;
  } | null>(null);
  const [explaining, setExplaining] = useState<AssessmentGridRow | null>(null);

  /* 1 — context, the initiatives and the business units the filter offers. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [contextPayload, listPayload, buPayload, runPayload] = await Promise.all([
          erpApi.context(),
          erpApi.initiatives.list({ limit: 200 }),
          /* No `activeOnly`: every business unit is active by definition (D-022). */
          erpApi.businessUnits.list(),
          /* EVERY run in the install — this screen's single control. A run names its own
             initiative, so the list does not wait for one to be chosen. */
          erpApi.assessmentGrids.listAll(),
        ]);
        if (cancelled) return;
        setContext(contextPayload.context);
        setInitiatives(listPayload.initiatives);
        setBusinessUnitsAll(buPayload.business_units);
        setRuns(runPayload.assessment_grids);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * 1b — THE MODELS OF THIS INITIATIVE. All of them are in force (`0106`), so this is a real
   * choice rather than a list with one legal entry. Newest first, which is the order
   * §6.10 returns and the order somebody looking for "the current one" expects.
   */
  const loadModels = useCallback(async () => {
    if (!initiativeId) {
      setModels([]);
      return;
    }
    try {
      const modelPayload = await erpApi.capabilityModels.listForInitiative(initiativeId);
      setModels(modelPayload.capability_models);
    } catch {
      /* A model list that cannot be read is not an error state: it only feeds the New
         Assessment Grid panel's picker, and the server re-validates the scope anyway. */
      setModels([]);
    }
  }, [initiativeId]);

  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  /* 2 — the functional areas of the chosen model (§7b.3). */
  const loadAreas = useCallback(async () => {
    if (!initiativeId) {
      setAreas([]);
      setGate(null);
      return;
    }
    try {
      const payload = await erpApi.assessmentGrid.areas(initiativeId, modelId || undefined);
      setAreas(payload.functional_areas);
      setGate(null);
    } catch (err) {
      setAreas([]);
      /* The gate, in the server's words. Any other failure is an error state. */
      if (err instanceof ErpApiError && err.code === 'capability_model_not_baselined') {
        setGate(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load this initiative’s model');
    }
  }, [initiativeId, modelId]);

  useEffect(() => {
    void loadAreas();
  }, [loadAreas]);

  /*
   * The area being assessed: the URL's if it names one, otherwise the first (the server orders
   * them by `sort_order`). DERIVED rather than written back, so this cannot loop — the same
   * arrangement the capability model editor uses for its version.
   */
  const areaId = run?.functional_area_capability_id ?? areaParam ?? areas[0]?.capability_id ?? '';
  const businessUnitId = run?.business_unit_id ?? businessUnitParam;

  /* 3 — the grid itself (§7b.4). */
  const loadGrid = useCallback(async () => {
    if (!initiativeId || !areaId) {
      setGrid(null);
      return;
    }
    setGridLoading(true);
    try {
      const payload = await erpApi.assessmentGrid.get(initiativeId, {
        functionalAreaCapabilityId: areaId,
        ...(modelId ? { capabilityModelId: modelId } : {}),
        ...(businessUnitId ? { businessUnitId } : {}),
        /* Without this the read matches every VERSION of the scope and interleaves them. */
        ...(gridParam ? { assessmentGridId: gridParam } : {}),
      });
      setGrid(payload);
      setGate(null);
      /* The preview needs the bands and factors; a failure here only loses the preview. */
      setScoringConfig(
        await erpApi.scoringConfig
          .get(initiativeId)
          .then((res) => res.scoring_config)
          .catch(() => null),
      );
    } catch (err) {
      setGrid(null);
      if (err instanceof ErpApiError && err.code === 'capability_model_not_baselined') {
        setGate(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load the assessment grid');
    } finally {
      setGridLoading(false);
    }
  }, [initiativeId, modelId, areaId, businessUnitId, gridParam]);

  useEffect(() => {
    void loadGrid();
  }, [loadGrid]);

  const initiative = initiatives.find((row) => row.id === initiativeId) ?? null;
  const baselined = initiative?.baselined_at !== null && initiative?.baselined_at !== undefined;
  /* §7b.6 answers `409 initiative_is_baselined`; the controls are not rendered either way. */
  const editable = canEnterAssessments(context) && !baselined;
  /* Creating a RUN is a different permission from entering numbers into one. */
  const canAssess = canEditCapabilityModel(context) && !baselined;

  const openNewRun = () => {
    setNewRunError(null);
    setNewRun({
      name: '',
      description: '',
      capabilityModelId: modelId || (models[0]?.id ?? ''),
      areaId: areaId || (areas[0]?.capability_id ?? ''),
      businessUnitId: businessUnitId || (businessUnitsAll[0]?.id ?? ''),
    });
  };

  const createRun = async () => {
    if (!newRun) return;
    setNewRunError(null);
    if (!newRun.capabilityModelId) return setNewRunError('Choose the capability model.');
    if (!newRun.areaId) return setNewRunError('Choose the functional area.');
    if (!newRun.businessUnitId) return setNewRunError('Choose the business unit.');
    try {
      const payload = await erpApi.assessmentGrids.create(initiativeId, {
        /* Omitted when blank — the server names it from the scope. */
        ...(newRun.name.trim() === '' ? {} : { name: newRun.name.trim() }),
        description: newRun.description.trim() || null,
        capability_model_id: newRun.capabilityModelId,
        functional_area_capability_id: newRun.areaId,
        business_unit_id: newRun.businessUnitId,
      });
      setNewRun(null);
      try {
        setRuns((await erpApi.assessmentGrids.listAll()).assessment_grids);
      } catch {
        /* The navigation below leaves this screen anyway; a stale pulldown behind it is not
           worth an error state. */
      }
      /*
       * STRAIGHT TO ADDING SYSTEMS. A run with no systems has no rows, so landing back on an
       * empty grid would be the same dead end this change exists to remove.
       */
      navigate(
        `/erp_consolidation/assessment-runs?initiative=${encodeURIComponent(initiativeId)}` +
          `&grid=${encodeURIComponent(payload.assessment_grid.id)}`,
      );
    } catch (err) {
      setNewRunError(err instanceof Error ? err.message : 'Could not create that run.');
    }
  };

  const capabilities = useMemo(() => grid?.capabilities ?? [], [grid]);
  const rows = useMemo(() => grid?.rows ?? [], [grid]);

  /** Each row's score, by unit — `scores` is positionally aligned with `rows` (§7b.4). */
  const scoreByUnit = useMemo(() => {
    const map = new Map<string, UnitScore>();
    (grid?.rows ?? []).forEach((row, index) => {
      const score = grid?.scores[index];
      if (score) map.set(row.id, score);
    });
    return map;
  }, [grid]);

  /**
   * The server's own overlap TERM per cell — where the value came from, and what was scored.
   *
   * Read rather than derived: `source` already distinguishes entered / override / not applicable
   * / missing, and `effectiveOverlapPctTenths` is the figure that actually entered the sum. A
   * screen recomputing either would be the second implementation D-009 forbids.
   */
  const termsByUnit = useMemo(() => {
    const map = new Map<string, Map<string, OverlapTerm>>();
    for (const score of scoreByUnit.values()) {
      const terms = score.breakdown?.overlap.terms ?? [];
      map.set(score.assessmentUnitId, new Map(terms.map((term) => [term.capabilityId, term])));
    }
    return map;
  }, [scoreByUnit]);

  const storedCell = (unit: AssessmentGridRow, capabilityId: string) =>
    unit.assessments.find((cell) => cell.capability_id === capabilityId);

  const thresholdTenths = grid?.scoring.overlapThresholdPctTenths ?? 0;
  const dirtyRows = draftedRows(drafts);

  /*
   * Restore this run's unsaved edits when the run changes, then keep them stored. `restoredFor`
   * stops the store effect writing the PREVIOUS run's edits under the new run's key during the
   * render in which the run changed.
   */
  const draftKey = gridParam === '' ? null : draftStorageKey(gridParam);
  const [restoredFor, setRestoredFor] = useState<string | null>(null);

  useEffect(() => {
    setEditing(null);
    if (draftKey === null) {
      setDrafts({});
      setRestoredFor(null);
      return;
    }
    const stored = readStoredDrafts(draftKey);
    setDrafts(stored);
    setRestoredFor(draftKey);
    const rows = draftedRows(stored).length;
    if (rows > 0) {
      setNotice({
        kind: 'err',
        text: `${rows} row${rows === 1 ? ' has' : 's have'} unsaved changes from earlier — press Save to keep ${rows === 1 ? 'it' : 'them'}, or Discard changes.`,
      });
    }
  }, [draftKey]);

  useEffect(() => {
    if (draftKey === null || restoredFor !== draftKey) return;
    writeStoredDrafts(draftKey, drafts);
  }, [drafts, draftKey, restoredFor]);

  /* Closing or refreshing the tab with edits pending: the browser's own "leave site?" prompt. */
  const hasUnsaved = dirtyRows.length > 0;
  useEffect(() => {
    if (!hasUnsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  /* ── Writes ───────────────────────────────────────────────────────────── */

  /**
   * §7b.6 — ONE REQUEST PER CHANGED ROW, each atomic.
   *
   * The precedent posts the whole matrix in one call (`RaciDefinition.tsx:311-326`) because its
   * endpoint takes one; this endpoint's granularity is one assessment unit, so the loop is the
   * faithful form of the same action. Failures are LISTED with the row they belong to rather
   * than counted — `ApplicationMasterDataTab.tsx:803-804`: *"'12 skipped' tells you something is
   * wrong, not which line to fix."*
   */
  const save = async () => {
    if (dirtyRows.length === 0) return;
    setBusy(true);
    setNotice(null);
    const remaining: Drafts = {};
    const failures: string[] = [];
    let saved = 0;

    for (const [unitId, cells] of dirtyRows) {
      const row = rows.find((candidate) => candidate.id === unitId);
      const label = row ? rowLabel(row) : unitId;
      try {
        await erpApi.assessments.putForUnit(initiativeId, unitId, cells);
        saved += 1;
      } catch (err) {
        remaining[unitId] = drafts[unitId] ?? {};
        failures.push(`${label}: ${err instanceof Error ? err.message : 'could not be saved'}`);
      }
    }

    setDrafts(remaining);
    await loadGrid();
    setBusy(false);
    setNotice(
      failures.length === 0
        ? { kind: 'ok', text: `Saved ${saved} row${saved === 1 ? '' : 's'}.` }
        : {
            kind: 'err',
            text: `Saved ${saved} row${saved === 1 ? '' : 's'}. ${failures.join(' · ')}`,
          },
    );
  };

  /** §7b.7 — clears the cell outright. Immediate, because a delete has nothing to batch with. */
  const clearCell = async (unit: AssessmentGridRow, capability: GridCapability) => {
    if (
      !window.confirm(
        `Clear “${capability.name}” for ${rowLabel(unit)}? Nothing will be recorded for that cell, ` +
          'and the score will count it as missing rather than as not applicable.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await erpApi.assessments.clearCell(initiativeId, unit.id, capability.id);
      setDrafts((prev) => {
        const row = { ...(prev[unit.id] ?? {}) };
        delete row[capability.id];
        return { ...prev, [unit.id]: row };
      });
      await loadGrid();
      setNotice({ kind: 'ok', text: 'Cell cleared.' });
    } catch (err) {
      setNotice({
        kind: 'err',
        text: err instanceof Error ? err.message : 'Could not clear that cell',
      });
    } finally {
      setBusy(false);
    }
  };

  /* ── Cell interactions ───────────────────────────────────────────────── */

  const typeOverlap = (unit: AssessmentGridRow, capability: GridCapability, text: string) => {
    setEditing({ key: `${unit.id}|${capability.id}`, text });
    const tenths = textToTenths(text);
    /*
     * AN EMPTY FIELD CLEARS THE VALUE; UNPARSEABLE TEXT CHANGES NOTHING.
     *
     * `textToTenths` returns `null` for both, and the two mean opposite things on the wire: §7b.6
     * reads an explicit `null` as "clear this column". Sending it for a half-typed entry would
     * blank a recorded percentage because somebody was still typing.
     */
    if (text.trim() !== '' && tenths === null) return;
    /* An OUT-OF-RANGE number IS sent, so §7b.6 names the refusal and the user reads the server's
       own wording rather than meeting a silent clamp. */
    setDrafts((prev) => withDraft(prev, unit.id, capability.id, { overlap_pct_tenths: tenths }));
  };

  /**
   * A COUNT typed straight into the cell — owner, 2026-10-09, from the grid prototype: the
   * systems and interfaces counts were reachable only through "…", so the numbers behind
   * complexity were invisible on the grid. Same rule as the overlap: empty clears, a half-typed
   * or non-whole entry changes nothing, and Save is what writes.
   */
  const typeCount = (
    unit: AssessmentGridRow,
    capability: GridCapability,
    field: 'systems_count' | 'interfaces_count',
    text: string,
  ) => {
    setEditing({ key: `${unit.id}|${capability.id}|${field}`, text });
    const trimmed = text.trim();
    if (trimmed !== '' && !/^\d+$/.test(trimmed)) return;
    const value = trimmed === '' ? null : Number(trimmed);
    setDrafts((prev) => withDraft(prev, unit.id, capability.id, { [field]: value }));
  };

  /** The cell as it now stands: what is stored, with this screen's unsaved draft on top. */
  const mergedCell = (unit: AssessmentGridRow, capabilityId: string) => {
    const stored = storedCell(unit, capabilityId);
    const draft = draftFor(drafts, unit.id, capabilityId);
    return { ...(stored ?? {}), ...(draft ?? {}) } as Partial<CapabilityAssessment>;
  };

  /** The factors and bands the server scores with, from the initiative's definition. */
  const factors: ComplexityFactors | null =
    scoringConfig === null
      ? null
      : {
          systemsFactorWeightTenths: scoringConfig.systems_factor_weight_tenths,
          interfacesFactorWeightTenths: scoringConfig.interfaces_factor_weight_tenths,
          systemsBandValuesTenths: scoringConfig.systems_band_values_pct_tenths,
          systemsBandBoundaries: scoringConfig.systems_band_upper_counts,
          interfacesBandValuesTenths: scoringConfig.interfaces_band_values_pct_tenths,
          interfacesBandBoundaries: scoringConfig.interfaces_band_upper_counts,
        };

  /** One cell's complexity from its effective counts, or null until both are recorded. */
  const cellComplexity = (cell: Partial<CapabilityAssessment>): number | null => {
    if (factors === null || cell.is_not_applicable === true) return null;
    const counts = effectiveCounts({
      systemsCount: cell.systems_count ?? null,
      systemsCountOverride: cell.systems_count_override ?? null,
      interfacesCount: cell.interfaces_count ?? null,
      interfacesCountOverride: cell.interfaces_count_override ?? null,
    });
    if (counts.systemsCount === null || counts.interfacesCount === null) return null;
    return capabilityComplexityTenThousandths(counts.systemsCount, counts.interfacesCount, factors);
  };

  /**
   * THE ROW'S SCORE WHILE IT IS BEING EDITED — the same package functions, the same inputs and
   * the same treatment of a missing value (0) as `scoring/score.ts`, so the preview is the score
   * the server will return once the row is saved. Shown only for a row with unsaved edits; a
   * saved row shows the server's own figures.
   */
  const previewScore = (
    unit: AssessmentGridRow,
  ): { overlap: number; complexity: number; quadrant: QuadrantName } | null => {
    if (grid === null || factors === null || scoringConfig === null) return null;
    const cells = grid.capabilities.map((capability) => ({
      capability,
      cell: mergedCell(unit, capability.id),
    }));
    const naOption = grid.scoring.naOption;
    const overlap = weightedOverlap(
      cells.map(({ capability, cell }) => ({
        weightTenths: capability.unit_weight_tenths,
        overlapTenths:
          effectiveOverlapTenths({
            overlapPctTenths: cell.overlap_pct_tenths ?? null,
            overlapOverridePctTenths: cell.overlap_override_pct_tenths ?? null,
          }) ?? 0,
        notApplicable: cell.is_not_applicable === true,
      })),
      naOption,
    );
    const complexity = complexityIndex(
      cells.map(({ capability, cell }) => {
        const counts = effectiveCounts({
          systemsCount: cell.systems_count ?? null,
          systemsCountOverride: cell.systems_count_override ?? null,
          interfacesCount: cell.interfaces_count ?? null,
          interfacesCountOverride: cell.interfaces_count_override ?? null,
        });
        return {
          weightTenths: capability.unit_weight_tenths,
          systemsCount: counts.systemsCount ?? 0,
          interfacesCount: counts.interfacesCount ?? 0,
          notApplicable: cell.is_not_applicable === true,
        };
      }),
      factors,
    );
    return {
      overlap: overlap.pctTenThousandths,
      complexity: complexity.pctTenThousandths,
      quadrant: quadrantOf(
        overlap.pctTenThousandths,
        complexity.pctTenThousandths,
        thresholdsFromTenths(
          scoringConfig.overlap_threshold_pct_tenths,
          scoringConfig.complexity_threshold_pct_tenths,
        ),
      ),
    };
  };

  /**
   * N/A, and the one extra field it has to carry.
   *
   * §7b.6 refuses `is_not_applicable` with an overlap beside it (`not_applicable_with_overlap`)
   * and names the remedy in the message: *"send `overlap_pct_tenths: null` in the same
   * request."* So the toggle does exactly that, in the same cell patch, and the refusal cannot
   * be reached from this control.
   */
  const toggleNotApplicable = (unit: AssessmentGridRow, capability: GridCapability) => {
    const stored = storedCell(unit, capability.id);
    const draft = draftFor(drafts, unit.id, capability.id);
    const current =
      draft !== undefined && Object.hasOwn(draft, 'is_not_applicable')
        ? draft.is_not_applicable === true
        : stored?.is_not_applicable === true;
    setDrafts((prev) =>
      withDraft(prev, unit.id, capability.id, {
        is_not_applicable: !current,
        ...(current ? {} : { overlap_pct_tenths: null, overlap_override_pct_tenths: null }),
      }),
    );
  };

  const openCell = (unit: AssessmentGridRow, capability: GridCapability) =>
    setDialog({
      unit,
      capability,
      form: seedForm(storedCell(unit, capability.id), draftFor(drafts, unit.id, capability.id)),
      error: null,
    });

  const applyDialog = () => {
    if (!dialog) return;
    const reason = missingOverrideReason(dialog.form);
    if (reason !== null) {
      setDialog({ ...dialog, error: reason });
      return;
    }
    const patch = formToPatch(dialog.form, storedCell(dialog.unit, dialog.capability.id));
    const { capability_id: _ignored, ...change } = patch;
    setDrafts((prev) => withDraft(prev, dialog.unit.id, dialog.capability.id, change));
    setDialog(null);
  };

  /**
   * Does the cell panel hold a change that closing would throw away?
   *
   * Measured, not tracked with a flag: `seedForm` is what the panel OPENED with, so re-running it
   * against the same two inputs reproduces that baseline exactly, and anything different is an
   * edit. `CellForm` is a flat object of strings built by that one function, so its keys are always
   * in the same order and comparing the serialised forms is sound — and a flag would have to be
   * cleared in every setter, which is how a dirty flag drifts out of step with the form.
   */
  const cellDirty =
    dialog !== null &&
    JSON.stringify(dialog.form) !==
      JSON.stringify(
        seedForm(
          storedCell(dialog.unit, dialog.capability.id),
          draftFor(drafts, dialog.unit.id, dialog.capability.id),
        ),
      );

  const setForm = (change: Partial<CellForm>) =>
    setDialog((prev) =>
      prev ? { ...prev, form: { ...prev.form, ...change }, error: null } : prev,
    );

  /* ── Render ───────────────────────────────────────────────────────────── */


  const renderCell = (unit: AssessmentGridRow, capability: GridCapability) => {
    const key = `${unit.id}|${capability.id}`;
    const stored = storedCell(unit, capability.id);
    const draft = draftFor(drafts, unit.id, capability.id);
    const term = termsByUnit.get(unit.id)?.get(capability.id);
    const cell = mergedCell(unit, capability.id);

    const notApplicable = cell.is_not_applicable === true;
    const shownTenths = cell.overlap_pct_tenths ?? null;

    /*
     * THE COLOUR FOLLOWS WHAT IS TYPED — owner, 2026-10-09, from the grid prototype. It used to
     * follow the last SAVED score, so a value typed in stayed white until Save and a reload.
     * Same three outcomes as `cellPair`: at/above the threshold, below it, nothing recorded.
     */
    const effective = notApplicable
      ? null
      : effectiveOverlapTenths({
          overlapPctTenths: shownTenths,
          overlapOverridePctTenths: cell.overlap_override_pct_tenths ?? null,
        });
    const pair: ChipPair = notApplicable
      ? CHIP_NEUTRAL
      : effective === null
        ? NOT_RECORDED
        : effective >= thresholdTenths
          ? CHIP_SUCCESS
          : CHIP_INFO;

    const unsaved = draft !== undefined && Object.keys(draft).length > 1;
    /*
     * With no term there is nothing to say about where the value came from — which happens only
     * when the whole unit could not be scored, and the frozen column says so for the row. A cell
     * that HAS a stored value is not labelled "empty" on the strength of a missing score.
     */
    const tag = unsaved ? 'unsaved' : term ? SOURCE_TAG[term.source] : stored ? null : 'empty';
    const complexity = cellComplexity(cell);

    const countField = (field: 'systems_count' | 'interfaces_count', label: string) => {
      const overrideField =
        field === 'systems_count' ? 'systems_count_override' : 'interfaces_count_override';
      const override = cell[overrideField] ?? null;
      const editKey = `${key}|${field}`;
      const value = cell[field] ?? null;
      return (
        <label className="ag-count">
          <span className="ag-count-label">{label}</span>
          <input
            className="ag-count-input"
            type="number"
            step="1"
            min={field === 'systems_count' ? '1' : '0'}
            inputMode="numeric"
            disabled={!editable || notApplicable}
            aria-label={`${label} for ${capability.name}, ${rowLabel(unit)}`}
            title={override !== null ? `Overridden to ${override} — open … to change it` : undefined}
            value={editing?.key === editKey ? editing.text : value === null ? '' : String(value)}
            onChange={(e) => typeCount(unit, capability, field, e.target.value)}
            onBlur={() => setEditing(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            onClick={(e) => e.stopPropagation()}
          />
          {override !== null && <span className="ag-count-override">→ {override}</span>}
        </label>
      );
    };

    return (
      <td key={capability.id} className="ag-cap-c">
        <div
          className={`ag-cell${unsaved ? ' is-unsaved' : ''}`}
          style={{ background: pair.bg, color: pair.fg }}
        >
          <input
            className="ag-cell-input"
            type="number"
            step="0.1"
            min="0"
            max="100"
            inputMode="decimal"
            disabled={!editable || notApplicable}
            aria-label={`Overlap for ${capability.name}, ${rowLabel(unit)}`}
            placeholder={notApplicable ? 'n/a' : 'overlap %'}
            title={
              term === undefined
                ? undefined
                : `Scored: ${tenthsToText(term.effectiveOverlapPctTenths)}% · ${term.source}` +
                  (term.overlapOverridePctTenths !== null
                    ? ` · overridden to ${tenthsToText(term.overlapOverridePctTenths)}%`
                    : '')
            }
            value={
              editing?.key === key
                ? editing.text
                : shownTenths === null
                  ? ''
                  : tenthsToText(shownTenths)
            }
            onChange={(e) => typeOverlap(unit, capability, e.target.value)}
            onBlur={() => setEditing(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            /* So typing in a cell never reaches the row it sits in — `BudgetPlan.tsx:296`. */
            onClick={(e) => e.stopPropagation()}
          />
          <div className="ag-counts">
            {countField('systems_count', 'Systems')}
            {countField('interfaces_count', 'Interfaces')}
          </div>
          <div className="ag-cell-foot">
            <span className="ag-cell-cx">
              {complexity !== null ? `complexity ${formatPct(complexity, 0)}` : ''}
            </span>
            <span className="ag-cell-btns">
              {tag && (
                <span
                  className={`ag-tag${unsaved ? ' unsaved' : ''}`}
                  title={tagTitle(term, unsaved)}
                >
                  {tag}
                </span>
              )}
              {editable && (
                <button
                  type="button"
                  className={`ag-cell-btn${notApplicable ? ' on' : ''}`}
                  aria-pressed={notApplicable}
                  title={
                    notApplicable
                      ? 'Not applicable to this unit — press again to make it assessable'
                      : 'Mark this capability as not applicable to this unit'
                  }
                  onClick={() => toggleNotApplicable(unit, capability)}
                >
                  N/A
                </button>
              )}
              <button
                type="button"
                className="ag-cell-btn"
                title="Overrides, confidence and comments for this cell"
                aria-label={`Open ${capability.name} for ${rowLabel(unit)}`}
                onClick={() => openCell(unit, capability)}
              >
                …
              </button>
              {editable && stored !== undefined && (
                <button
                  type="button"
                  className="ag-cell-btn"
                  title="Clear this cell — nothing recorded, which is not the same as not applicable"
                  aria-label={`Clear ${capability.name} for ${rowLabel(unit)}`}
                  disabled={busy}
                  onClick={() => void clearCell(unit, capability)}
                >
                  ✕
                </button>
              )}
            </span>
          </div>
        </div>
      </td>
    );
  };

  return (
    <ErpScreen>
      <div className="page ag-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={AssessmentGridHelp} />
        {/*
          THE PRIMARY ACTION SITS IN THE SCREEN HEADER, which is this module's convention and
          the owner's instruction, 2026-10-08: *"see where '+ New Assessment Grid' button should
          be positioned - at the screen header"*. `BusinessUnitHierarchy` puts Add Business Unit
          there and `CapabilityModelEditor` puts + New Model there; a create action anywhere else
          on the screen is the odd one out.
        */}
        <PageHeader
          title="Assessment Grid"
          subtitle="One row per business unit × system, one column per capability — the overlap percentages every score is computed from"
          actions={
            canAssess && initiativeId !== '' && gate === null ? (
              <button type="button" className="erp-rl-btn erp-rl-btn--primary" onClick={openNewRun}>
                <i className="material-icons">add</i>
                New Assessment Grid
              </button>
            ) : null
          }
        />

        {/*
          ONE CONTROL: THE RUN.

          Owner, 2026-10-08: *"I should be able to only see the Assessment Run as pull down -
          not those 4 fields I am currently looking at"*.

          Initiative, capability model, functional area and business unit were four separate
          pickers here. They are the run's DEFINITION — fixed when it is created — so asking for
          them again on the screen that reads the run was asking four questions the answer
          already contains, and letting them drift out of step with it. The run names its own
          initiative, which is why even that one is gone.
        */}
        <div className="ag-toolbar">
          <SelectField
            label="Assessment run"
            width="lg"
            id="erp-grid-run"
            value={gridParam}
            placeholder={runs.length === 0 ? '— none yet —' : '— Select an Assessment Run —'}
            options={runs.map((row) => ({
              value: row.id,
              id: row.name,
              /* The whole definition on one line, so the pulldown answers "which run is this?"
                 without the four controls it replaced. */
              description:
                `${row.initiative_name ?? '?'} · ${row.capability_model_name ?? '?'} v${row.capability_model_version ?? '?'} · ` +
                `${row.system_count} system${row.system_count === 1 ? '' : 's'}` +
                `${row.assessed_count > 0 ? ` · ${row.assessed_count} recorded` : ''}`,
            }))}
            onChange={(value) => {
              /* No longer discards: this run's unsaved edits stay stored and come back with it. */
              setNotice(null);
              setSearchParams(value ? { grid: value } : {});
            }}
          />

          {grid !== null && (
            <div className="ag-actions">
              {editable && (
                <>
                  <button
                    type="button"
                    className="ag-rolebtn rm"
                    disabled={busy || dirtyRows.length === 0}
                    title="Throw away every unsaved edit on this screen"
                    onClick={() => {
                      setDrafts({});
                      setEditing(null);
                      setNotice(null);
                    }}
                  >
                    Discard changes
                  </button>
                  <button
                    type="button"
                    className="ag-primary"
                    disabled={busy || dirtyRows.length === 0}
                    title={
                      dirtyRows.length === 0
                        ? 'Nothing has changed yet'
                        : `Save ${dirtyRows.length} changed row${dirtyRows.length === 1 ? '' : 's'}`
                    }
                    onClick={() => void save()}
                  >
                    {busy
                      ? 'Saving…'
                      : dirtyRows.length === 0
                        ? 'Save'
                        : `Save ${dirtyRows.length} row${dirtyRows.length === 1 ? '' : 's'}`}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {grid !== null && (
          <div className="ag-legendbar">
            <div className="ag-legend">
              <span className="ag-lg-chip">
                <span
                  className="ag-lg-key"
                  style={{ background: CHIP_SUCCESS.bg, color: CHIP_SUCCESS.fg }}
                >
                  ≥
                </span>{' '}
                At or above the overlap threshold ({tenthsToText(thresholdTenths)}%)
              </span>
              <span className="ag-lg-chip">
                <span
                  className="ag-lg-key"
                  style={{ background: CHIP_INFO.bg, color: CHIP_INFO.fg }}
                >
                  &lt;
                </span>{' '}
                Below it
              </span>
              <span className="ag-lg-chip">
                <span
                  className="ag-lg-key"
                  style={{ background: CHIP_NEUTRAL.bg, color: CHIP_NEUTRAL.fg }}
                >
                  N/A
                </span>{' '}
                Not applicable — scored by the{' '}
                {grid.scoring.naOption === 'zero' ? '0%' : 'renormalize'} rule
              </span>
              <span className="ag-lg-chip">
                <span
                  className="ag-lg-key"
                  style={{
                    background: NOT_RECORDED.bg,
                    color: NOT_RECORDED.fg,
                    border: '1px solid #cfd8dc',
                  }}
                >
                  —
                </span>{' '}
                Nothing recorded — counted as 0% and flagged
              </span>
              <span className="ag-lg-chip">
                <span className="ag-tag">override</span> A figure replaced, with its reason
              </span>
            </div>
            {notice !== null && <div className={`ag-notice ${notice.kind}`}>{notice.text}</div>}
            {baselined && (
              <div className="ag-notice err">
                This initiative is baselined, so its assessments are read only. The server refuses a
                write either way.
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="ag-empty">Loading…</div>
        ) : error !== null ? (
          <div className="ag-empty">
            <h3>⚠️ {error}</h3>
            <button type="button" className="ag-rolebtn" onClick={() => void loadGrid()}>
              Retry
            </button>
          </div>
        ) : !initiativeId ? (
          <div className="ag-empty">
            <h3>Select an Initiative</h3>
            <p>
              Pick an initiative above. The grid covers one functional area of its baselined
              capability model at a time.
            </p>
          </div>
        ) : gate !== null ? (
          /* §6.1's gate, in the server's own words — one code, three messages (§7b.0). */
          <div className="ag-gate">
            <div className="ag-gate__text">
              <div className="ag-gate__title">Assessment is not open yet</div>
              <div className="ag-gate__sub">{gate}</div>
            </div>
            {/* `navigate`, never an `<a href>`: this is one SPA and a real link would reload
                the whole shell (`InitiativesList.tsx:200-206`). */}
            <button
              type="button"
              className="ag-rolebtn"
              onClick={() =>
                navigate(
                  `/erp_consolidation/capability-model?initiative=${encodeURIComponent(initiativeId)}`,
                )
              }
            >
              Open the capability model
            </button>
          </div>
        ) : gridLoading ? (
          <div className="ag-empty">Loading the grid…</div>
        ) : rows.length === 0 ? (
          /*
            THE EMPTY STATE TELLS YOU WHICH EMPTY IT IS, AND CARRIES THE WAY OUT.
            
            It used to say "Nothing is scoped … yet. Assessment units are created when systems
            are selected for the initiative" — true, and useless: it named no screen, offered no
            control, and read identically whether there was no run at all, a run with no systems,
            or a filter matching nothing. The owner hit it four times in a row and asked, fairly,
            *"How the fuck I create a Assessment model on Assessment grid?"* The answer was "on a
            different screen", which is a design failure, not an explanation.
            
            Three distinct states, each with the action that resolves it.
          */
          <div className="ag-empty">
            {runs.length === 0 ? (
              <>
                <h3>No assessment runs yet</h3>
                <p>
                  A <strong>run</strong> is one business unit&rsquo;s systems, assessed for one
                  functional area. Create one, put systems in it, and its rows appear here.
                </p>
              </>
            ) : run === null ? (
              <>
                <h3>Select an assessment run</h3>
                <p>
                  This initiative has {runs.length} run{runs.length === 1 ? '' : 's'}. Pick one
                  above, or create another.
                </p>
              </>
            ) : (
              <>
                <h3>This run has no systems yet</h3>
                <p>
                  <strong>{run.name}</strong> covers{' '}
                  {run.business_unit_name} &middot; {run.functional_area_name}, and no systems
                  have been put in it. Add the systems that business unit runs for that area and
                  they become the rows of this grid.
                </p>
                <button
                  type="button"
                  className="ag-rolebtn"
                  style={{ marginTop: 12 }}
                  onClick={() =>
                    navigate(
                      `/erp_consolidation/assessment-runs?initiative=${encodeURIComponent(initiativeId)}` +
                        `&grid=${encodeURIComponent(run.id)}`,
                    )
                  }
                >
                  Add systems to this run
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="ag-scroll">
            <table className="ag-matrix">
              <thead>
                <tr>
                  <th className="ag-unith">Assessment unit</th>
                  {capabilities.map((capability) => (
                    <th
                      key={capability.id}
                      className="ag-caph"
                      /* The two weights are different quantities, and §6.1 is about the first:
                         `unit_weight_tenths` is this unit's share (the set totals 100.0%),
                         `effective_weight_tenths` is the whole model's floored product (§3.3). */
                      title={
                        `${capability.name} — ${tenthsToText(capability.unit_weight_tenths)}% of this unit, ` +
                        `${tenthsToText(capability.effective_weight_tenths)}% of the whole model`
                      }
                    >
                      {capability.code && <span className="ag-cap-code">{capability.code}</span>}
                      <span className="ag-cap-name">{capability.name}</span>
                      <span className="ag-cap-weight">
                        {tenthsToText(capability.unit_weight_tenths)}%
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((unit) => {
                  const score = scoreByUnit.get(unit.id) ?? null;
                  const quadrant = score ? QUADRANT_CHIP[score.quadrant] : null;
                  /* A row with unsaved edits shows what Save WILL produce, marked as a preview. */
                  const rowDirty = Object.keys(drafts[unit.id] ?? {}).length > 0;
                  const preview = rowDirty ? previewScore(unit) : null;
                  return (
                    <tr key={unit.id}>
                      <td className="ag-unit">
                        <div className="ag-unit-box">
                          <span className="ag-unit-primary">
                            {unit.business_unit_name ?? unit.business_unit_id}
                          </span>
                          <span className="ag-unit-secondary">
                            {[unit.application_id_code, unit.application_name]
                              .filter((part) => part)
                              .join(' · ') || unit.application_id}
                          </span>
                          <div className="ag-unit-scores">
                            {preview !== null && (
                              <>
                                <span
                                  className="ag-chip"
                                  style={{ background: CHIP_INFO.bg, color: CHIP_INFO.fg }}
                                  title="Weighted overlap if this row is saved as it is now"
                                >
                                  Overlap {formatPct(preview.overlap, 1)}
                                </span>
                                <span
                                  className="ag-chip"
                                  style={{ background: CHIP_NEUTRAL.bg, color: CHIP_NEUTRAL.fg }}
                                  title="Complexity if this row is saved as it is now"
                                >
                                  Complexity {formatPct(preview.complexity, 1)}
                                </span>
                                <span
                                  className="ag-chip"
                                  style={{
                                    background: QUADRANT_CHIP[preview.quadrant].pair.bg,
                                    color: QUADRANT_CHIP[preview.quadrant].pair.fg,
                                  }}
                                  title={QUADRANT_CHIP[preview.quadrant].meaning}
                                >
                                  {preview.quadrant}
                                </span>
                                <span className="ag-preview">Preview — press Save to keep</span>
                              </>
                            )}
                            {preview === null && score !== null && (
                              <>
                                <span
                                  className="ag-chip"
                                  style={{ background: CHIP_INFO.bg, color: CHIP_INFO.fg }}
                                  title="Weighted overlap for this unit"
                                >
                                  {score.overlapDisplay}
                                </span>
                                <span
                                  className="ag-chip"
                                  style={{ background: CHIP_NEUTRAL.bg, color: CHIP_NEUTRAL.fg }}
                                  title="Complexity for this unit"
                                >
                                  Complexity {score.complexityDisplay}
                                </span>
                                {quadrant !== null && (
                                  <span
                                    className="ag-chip"
                                    style={{
                                      background: quadrant.pair.bg,
                                      color: quadrant.pair.fg,
                                    }}
                                    title={quadrant.meaning}
                                  >
                                    {score.quadrant}
                                  </span>
                                )}
                                {!score.completeness.complete && (
                                  <span
                                    className="ag-tag"
                                    title={`${score.completeness.cellsPresent} of ${score.completeness.capabilities} cells recorded, ${score.completeness.missing} missing`}
                                  >
                                    {tenthsToText(score.completeness.completeTenths)}% complete
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="ag-link"
                                  onClick={() => setExplaining(unit)}
                                >
                                  Explain
                                </button>
                              </>
                            )}
                            {preview === null && score === null && (
                              <span className="ag-tag" title="This unit could not be scored">
                                not scored
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {capabilities.map((capability) => renderCell(unit, capability))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── One cell, in full. SCREEN_MAP #10's reason-required override form. ─────────
            A RIGHT-SIDE PANEL since DECISIONS.md D-017 — *"in general please replace all windows
            from this app by 'right panel'"*. It matters more here than anywhere: the grid behind
            is the reason this form is open, and the modal covered it, so there was no way to see
            the neighbouring cells while deciding this one. The panel does not dim, so the row
            stays readable.

            The IDENTITY LINE moves into the panel header, which is what that header is for: the
            capability is the title, and the unit it crosses is the line under it — the same text
            `.ag-dialog-sub` carried, word for word, in the same order. ─────────────────────── */}
        {dialog !== null && (
          <FormPanel
            open
            title={dialog.capability.name}
            subtitle={`${rowLabel(dialog.unit)} · ${dialog.unit.functional_area_name ?? ''} · ${tenthsToText(
              dialog.capability.unit_weight_tenths,
            )}% of this unit`}
            onRequestClose={() => setDialog(null)}
            onSave={applyDialog}
            saveLabel="Apply"
            /* Apply stages; the toolbar's Save writes. The glyph would contradict the hint at the
               foot of the body, so it is suppressed — `FormPanel.tsx` records why. */
            showSaveIcon={false}
            hideSave={!editable}
            dirty={cellDirty}
            discardMessage="Discard your changes to this cell?"
          >
            {dialog.error !== null && <div className="erp-panel-error">⚠️ {dialog.error}</div>}

            <div className="form-group">
              <label htmlFor="erp-cell-na">
                <input
                  id="erp-cell-na"
                  type="checkbox"
                  checked={dialog.form.is_not_applicable}
                  disabled={!editable}
                  onChange={(e) =>
                    setForm(
                      e.target.checked
                        ? { is_not_applicable: true, overlap: '', overlapOverride: '' }
                        : { is_not_applicable: false },
                    )
                  }
                />{' '}
                Not applicable to this unit
              </label>
              <p className="ag-field-hint">
                A <strong>statement</strong> that this capability does not apply here, scored by the
                N/A rule on the Scoring Definition screen. It is not the same as an empty cell,
                which is scored as 0% and flagged as missing.
              </p>
            </div>

            <div className="ag-override-row">
              <div className="form-group">
                <label htmlFor="erp-cell-overlap">Overlap %</label>
                <input
                  id="erp-cell-overlap"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  inputMode="decimal"
                  value={dialog.form.overlap}
                  disabled={!editable || dialog.form.is_not_applicable}
                  onChange={(e) => setForm({ overlap: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-overlap-ovr">Override %</label>
                <input
                  id="erp-cell-overlap-ovr"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  inputMode="decimal"
                  value={dialog.form.overlapOverride}
                  disabled={!editable || dialog.form.is_not_applicable}
                  onChange={(e) => setForm({ overlapOverride: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-overlap-why">Reason for the override</label>
                <input
                  id="erp-cell-overlap-why"
                  type="text"
                  value={dialog.form.overlapOverrideReason}
                  disabled={!editable}
                  placeholder="Required whenever an override is set"
                  onChange={(e) => setForm({ overlapOverrideReason: e.target.value })}
                />
              </div>
            </div>

            <div className="ag-override-row">
              <div className="form-group">
                <label htmlFor="erp-cell-systems">Systems supporting it</label>
                <input
                  id="erp-cell-systems"
                  type="number"
                  min="0"
                  step="1"
                  value={dialog.form.systems}
                  disabled={!editable}
                  onChange={(e) => setForm({ systems: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-systems-ovr">Override</label>
                <input
                  id="erp-cell-systems-ovr"
                  type="number"
                  min="0"
                  step="1"
                  value={dialog.form.systemsOverride}
                  disabled={!editable}
                  onChange={(e) => setForm({ systemsOverride: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-systems-why">Reason for the override</label>
                <input
                  id="erp-cell-systems-why"
                  type="text"
                  value={dialog.form.systemsOverrideReason}
                  disabled={!editable}
                  placeholder="Required whenever an override is set"
                  onChange={(e) => setForm({ systemsOverrideReason: e.target.value })}
                />
              </div>
            </div>

            <div className="ag-override-row">
              <div className="form-group">
                <label htmlFor="erp-cell-interfaces">Interfaces</label>
                <input
                  id="erp-cell-interfaces"
                  type="number"
                  min="0"
                  step="1"
                  value={dialog.form.interfaces}
                  disabled={!editable}
                  onChange={(e) => setForm({ interfaces: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-interfaces-ovr">Override</label>
                <input
                  id="erp-cell-interfaces-ovr"
                  type="number"
                  min="0"
                  step="1"
                  value={dialog.form.interfacesOverride}
                  disabled={!editable}
                  onChange={(e) => setForm({ interfacesOverride: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-interfaces-why">Reason for the override</label>
                <input
                  id="erp-cell-interfaces-why"
                  type="text"
                  value={dialog.form.interfacesOverrideReason}
                  disabled={!editable}
                  placeholder="Required whenever an override is set"
                  onChange={(e) => setForm({ interfacesOverrideReason: e.target.value })}
                />
              </div>
            </div>
            <p className="ag-field-hint">
              The systems and interfaces counts are what <strong>complexity</strong> is banded from
              — 0.4 × the systems band plus 0.6 × the interfaces band by default, both configurable
              on the Scoring Definition screen.
            </p>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="erp-cell-confidence">Confidence %</label>
                <input
                  id="erp-cell-confidence"
                  type="number"
                  step="0.1"
                  min="1"
                  max="100"
                  inputMode="decimal"
                  value={dialog.form.confidence}
                  disabled={!editable}
                  onChange={(e) => setForm({ confidence: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="erp-cell-comments">Comments</label>
                <textarea
                  id="erp-cell-comments"
                  rows={3}
                  value={dialog.form.comments}
                  disabled={!editable}
                  onChange={(e) => setForm({ comments: e.target.value })}
                />
              </div>
            </div>

            {/* The hint stays with the control it explains. It sat under the actions in the
                    modal; the actions are now the panel's footer, so it sits at the foot of the
                    body immediately above them — the same sentence in the same place relative to
                    the button it is about. */}
            {editable && (
              <p className="ag-field-hint">
                <strong>Apply</strong> holds the change on this screen. <strong>Save</strong> in the
                toolbar writes it — the whole row at once, or none of it.
              </p>
            )}
          </FormPanel>
        )}

        {explaining !== null && (
          <CalculationBreakdown
            initiativeId={initiativeId}
            assessmentUnitId={explaining.id}
            onClose={() => setExplaining(null)}
          />
        )}

        {/* A NEW RUN, created from the screen where the need is discovered. */}
        {newRun && (
          <FormPanel
            open
            title="New Assessment Grid"
            subtitle={initiative?.name}
            onRequestClose={() => {
              setNewRun(null);
              setNewRunError(null);
            }}
            onSave={() => void createRun()}
            saveLabel="Create Run"
            busyLabel="Creating…"
            dirty={newRun.name.trim() !== '' || newRun.description.trim() !== ''}
            discardMessage="Discard this run?"
          >
            {newRunError && (
              <p className="fb-panel-error" role="alert">
                {newRunError}
              </p>
            )}
            {/*
              THE FOUR FIELDS ARE THE DEFINITION — owner, 2026-10-08: *"for Assessment I need to
              have all 4 fields on Assessment grid as a part of the definition"*, after correctly
              calling the typed name a duplicate of the initiative.

              Initiative is first and READ-ONLY here: the screen is already in one, and offering
              it as a choice would let somebody create a run against an initiative they are not
              looking at.
            */}
            <div className="form-group">
              <label>Initiative</label>
              <p className="ag-defn">{initiative?.name ?? '—'}</p>
            </div>
            <div className="form-group">
              <SelectField
                label="Capability model"
                id="erp-ag-run-model"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newRun.capabilityModelId}
                options={models.map((row) => ({
                  value: row.id,
                  id: row.name,
                  description: `v${row.version} · ${row.capability_count} capabilities${row.weights_balanced ? '' : ' · weights do not total 100.0%'}`,
                  disabled: !row.weights_balanced,
                }))}
                onChange={(value) =>
                  setNewRun((prev) => (prev ? { ...prev, capabilityModelId: value } : prev))
                }
              />
            </div>
            <div className="form-group">
              <SelectField
                label="Functional area"
                id="erp-ag-run-area"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newRun.areaId}
                options={areas.map((row) => ({
                  value: row.capability_id,
                  id: row.code ?? row.name,
                  description: `${row.name} · ${row.capability_count} capabilities`,
                }))}
                onChange={(value) => setNewRun((prev) => (prev ? { ...prev, areaId: value } : prev))}
              />
            </div>
            <div className="form-group">
              <SelectField
                label="Business unit"
                id="erp-ag-run-bu"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                allowClear={false}
                value={newRun.businessUnitId}
                options={businessUnitsAll.map((row) => ({ value: row.id, id: row.name }))}
                onChange={(value) =>
                  setNewRun((prev) => (prev ? { ...prev, businessUnitId: value } : prev))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-ag-run-name">Label (optional)</label>
              <input
                id="erp-ag-run-name"
                type="text"
                value={newRun.name}
                onChange={(e) =>
                  setNewRun((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="Left blank, it is named from the business unit and area"
              />
              <p className="ag-hint">
                The four fields above identify this run. A label is only for the rare case you
                want to call it something else.
              </p>
            </div>
            <p className="ag-hint">
              Creating the run takes you straight to choosing its systems — a run with no systems
              has no rows to assess.
            </p>
          </FormPanel>
        )}
      </div>
    </ErpScreen>
  );
}

/** `"<BU> × <system>"` — the same label the server's audit entries use for a cell. */
function rowLabel(unit: AssessmentGridRow): string {
  return [
    unit.business_unit_name ?? unit.business_unit_id,
    unit.application_name ?? unit.application_id_code ?? unit.application_id,
  ].join(' × ');
}

function tagTitle(term: OverlapTerm | undefined, unsaved: boolean): string | undefined {
  if (unsaved) return 'Changed on this screen and not written yet';
  if (term === undefined) return undefined;
  if (term.source === 'missing') {
    return 'Nothing recorded — scored as 0% in the lowest complexity band, and counted';
  }
  if (term.source === 'not_applicable') return 'Not applicable to this unit';
  if (term.source === 'override') {
    return `Overridden to ${tenthsToText(term.effectiveOverlapPctTenths)}%`;
  }
  return undefined;
}
